import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../supabaseClient';
import './EditionsPage.css';

const EditionsPage = () => {
  const [upcomingEditions, setUpcomingEditions] = useState([]);
  const [pastEditions, setPastEditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEditions = async () => {
      try {
        const now = new Date().toISOString();

        const { data: upcoming, error: upcomingError } = await supabase
          .from('editions')
          .select('*')
          .gte('event_date', now)
          .order('event_date', { ascending: true });

        const { data: past, error: pastError } = await supabase
          .from('editions')
          .select('*')
          .lt('event_date', now)
          .order('event_date', { ascending: false });

        if (upcomingError) throw upcomingError;
        if (pastError) throw pastError;

        setUpcomingEditions(upcoming || []);
        setPastEditions(past || []);
      } catch (error) {
        console.error('Error fetching editions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEditions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate().toString().padStart(2, '0'),
      month: date.toLocaleDateString('fr-FR', { month: 'short' }),
      year: date.getFullYear(),
    };
  };

  const truncateDescription = (text, lines = 2) => {
    if (!text) return '';
    const lineArray = text.split('\n');
    return lineArray.slice(0, lines).join('\n');
  };

  if (loading) {
    return (
      <div className="editions-page">
        <div className="editions-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="editions-page">
      <div className="editions-container">
        <h1 className="editions-title">Les Éditions</h1>

        {/* Upcoming Editions */}
        {upcomingEditions.length > 0 && (
          <section className="editions-section upcoming-section">
            <h2>À venir</h2>
            <div className="upcoming-grid">
              {upcomingEditions.map((edition) => {
                const dateInfo = formatDate(edition.event_date);
                return (
                  <div key={edition.id} className="upcoming-card">
                    <div className="upcoming-date">
                      <div className="date-day">{dateInfo.day}</div>
                      <div className="date-rest">
                        <div className="date-month">{dateInfo.month}</div>
                        <div className="date-year">{dateInfo.year}</div>
                      </div>
                    </div>
                    <div className="upcoming-info">
                      <h4>{edition.title}</h4>
                      {edition.location && <p className="location">{edition.location}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Past Editions */}
        {pastEditions.length > 0 && (
          <section className="editions-section past-section">
            <h2>Éditions passées</h2>
            <div className="past-editions-list">
              {pastEditions.map((edition) => {
                const dateInfo = formatDate(edition.event_date);
                return (
                  <Link
                    key={edition.id}
                    to={`/members/editions/${edition.id}`}
                    className="past-edition-card"
                  >
                    {edition.cover_image && (
                      <div className="past-cover">
                        <img src={edition.cover_image} alt={edition.title} />
                      </div>
                    )}
                    <div className="past-content">
                      <div className="past-date">
                        {dateInfo.month} {dateInfo.day}, {dateInfo.year}
                      </div>
                      <h3>{edition.title}</h3>
                      {edition.description && (
                        <p className="past-description">
                          {truncateDescription(edition.description, 2)}
                        </p>
                      )}
                      <span className="view-details">Voir le détail →</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {upcomingEditions.length === 0 && pastEditions.length === 0 && (
          <p className="no-editions">Aucune édition disponible</p>
        )}
      </div>

      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #1f5d5b;
          border-top: 4px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EditionsPage;
